/**
 * Cola de subida de archivos de las Notas de un aseo (round 2026-10-26).
 *
 * Es un STORE a nivel de módulo (no estado de React) por dos razones:
 *  - Si la aseadora cierra el modal/panel de notas con un video de 300 MB a
 *    medias, la subida NO se pierde: sigue corriendo y, al reabrir las notas,
 *    ve el avance (o el archivo ya listo).
 *  - `beforeunload` y el aviso de "pantalla bloqueada" son globales.
 *
 * Concurrencia: UN archivo a la vez (en celular dos subidas en paralelo solo
 * se estorban el ancho de banda). Cada ítem conserva su `File` en memoria para
 * que Cancelar / Reintentar no obliguen a elegirlo de nuevo.
 *
 * La lógica de estados (reducer, ETA, formato) vive en `utils/colaSubida.ts`;
 * aquí solo se orquesta con la red (`utils/subidaReanudable.ts`).
 */

import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  colaReducer,
  hayActivos,
  kindDeArchivo,
  mimeDeArchivo,
  siguientePendiente,
  type AccionCola,
  type EstadoItemCola,
  type ItemCola,
} from "../../../utils/colaSubida";
import { compressImageFile } from "../../../utils/imageCompression";
import { HeicError } from "../../../utils/heic";
import { esCancelacion, mensajeErrorRed } from "../../../utils/erroresRed";
import {
  descartarSubida,
  esErrorNoReintentable,
  subirArchivoAModulo,
  type ProgresoSubida,
} from "../../../utils/subidaReanudable";
import { rutaArchivosNota, type AseoNoteTipo } from "../../../services/aseos.api";

export type ItemNota = ItemCola<AseoNoteTipo>;

/** Lo que el procesador recibe para reportar avance a la cola. */
export interface ContextoProcesar {
  signal: AbortSignal;
  onEstado: (estado: EstadoItemCola, mensaje?: string | null) => void;
  onProgreso: (p: ProgresoSubida) => void;
}

export interface DepsCola {
  /** Hace el trabajo de un ítem (comprimir + subir). Debe rechazar si falla. */
  procesar: (item: ItemNota, ctx: ContextoProcesar) => Promise<unknown>;
  /**
   * Libera lo que el ítem dejó en el servidor (la sesión de subida reanudable)
   * cuando se Cancela o Quita estando en error/cancelado. Mejor esfuerzo.
   */
  descartar?: (item: ItemNota) => void | Promise<void>;
  crearPreview?: (file: File) => string | null;
  revocarPreview?: (url: string) => void;
  ahora?: () => number;
  /** Ms que un ítem "listo" permanece visible antes de salir de la cola. */
  quitarListoMs?: number;
}

const ESTADO_DE_SUBIDA: Record<ProgresoSubida["estado"], EstadoItemCola> = {
  preparando: "subiendo",
  subiendo: "subiendo",
  reintentando: "reintentando",
  sin_conexion: "sin_conexion",
  procesando: "procesando",
  completa: "procesando",
};

export function crearColaSubida(deps: DepsCola) {
  const ahora = deps.ahora ?? (() => Date.now());
  const quitarListoMs = deps.quitarListoMs ?? 4000;
  const crearPreview =
    deps.crearPreview ??
    ((f: File) => {
      try {
        return f.type.startsWith("image/") ? URL.createObjectURL(f) : null;
      } catch {
        return null;
      }
    });
  const revocarPreview =
    deps.revocarPreview ??
    ((u: string) => {
      try {
        URL.revokeObjectURL(u);
      } catch {
        /* nada */
      }
    });

  let items: ItemNota[] = [];
  let avisoPantalla = false;
  let corriendo = false;
  let contador = 0;
  let escuchandoUnload = false;
  const suscriptores = new Set<() => void>();
  const oyentesListo = new Set<(i: ItemNota) => void>();
  const controladores = new Map<string, AbortController>();

  const avisar = () => suscriptores.forEach((fn) => fn());

  // ── beforeunload: evita cerrar/recargar la pestaña con subidas en curso ──
  const alDescargar = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = ""; // algunos navegadores lo exigen para mostrar el diálogo
    return "";
  };
  // ── Pantalla bloqueada / cambio de app durante una subida ──
  const alCambiarVisibilidad = () => {
    if (document.visibilityState === "hidden" && hayActivos(items)) {
      if (!avisoPantalla) {
        avisoPantalla = true;
        avisar();
      }
    }
  };

  const sincronizarGlobales = () => {
    const activos = hayActivos(items);
    if (activos && !escuchandoUnload && typeof window !== "undefined") {
      window.addEventListener("beforeunload", alDescargar);
      document.addEventListener("visibilitychange", alCambiarVisibilidad);
      escuchandoUnload = true;
    } else if (!activos && escuchandoUnload) {
      window.removeEventListener("beforeunload", alDescargar);
      document.removeEventListener("visibilitychange", alCambiarVisibilidad);
      escuchandoUnload = false;
      if (avisoPantalla) {
        avisoPantalla = false;
      }
    }
  };

  const dispatch = (a: AccionCola) => {
    const antes = items;
    items = colaReducer(items, a) as ItemNota[];
    if (items !== antes) {
      sincronizarGlobales();
      avisar();
    }
  };

  /** Un ítem en error o cancelado deja de ser reanudable: se libera su sesión en el servidor. */
  const liberarSesion = (it: ItemNota | undefined) => {
    if (!it || (it.estado !== "error" && it.estado !== "cancelado")) return;
    try {
      void Promise.resolve(deps.descartar?.(it)).catch(() => undefined);
    } catch {
      /* mejor esfuerzo */
    }
  };

  const quitarSilencioso = (id: string) => {
    const it = items.find((i) => i.id === id);
    if (it?.previewUrl) revocarPreview(it.previewUrl);
    dispatch({ tipo: "quitar", id });
  };

  async function ejecutar(it: ItemNota): Promise<void> {
    const ctrl = new AbortController();
    controladores.set(it.id, ctrl);
    dispatch({ tipo: "estado", id: it.id, estado: it.kind === "image" ? "comprimiendo" : "subiendo" });
    try {
      await deps.procesar(it, {
        signal: ctrl.signal,
        onEstado: (estado, mensaje) => dispatch({ tipo: "estado", id: it.id, estado, mensaje }),
        onProgreso: (p) =>
          dispatch({
            tipo: "progreso",
            id: it.id,
            bytes: p.bytes,
            total: p.total,
            porcentaje: p.porcentaje,
            velocidadBps: p.velocidadBps,
            etaSeg: p.etaSeg,
            estado: ESTADO_DE_SUBIDA[p.estado],
            mensaje: p.mensaje ?? null,
            intentos: p.intento,
          }),
      });
      // Si la cancelaron justo al terminar, el reducer lo respeta ("cancelado" gana).
      dispatch({ tipo: "listo", id: it.id });
      const listo = items.find((i) => i.id === it.id);
      if (listo?.estado === "listo") {
        oyentesListo.forEach((fn) => fn(listo));
        setTimeout(() => quitarSilencioso(it.id), quitarListoMs);
      }
    } catch (e) {
      // Si Reintentar ya relanzó este ítem mientras el intento anterior terminaba de
      // abortarse, este catch es de una ejecución obsoleta: no debe pisar la nueva.
      if (controladores.get(it.id) !== ctrl) return;
      if (esCancelacion(e) || ctrl.signal.aborted) {
        dispatch({ tipo: "cancelar", id: it.id });
      } else {
        dispatch({
          tipo: "error",
          id: it.id,
          mensaje: mensajeErrorRed(e, "No se pudo subir el archivo."),
          noReintentable: esErrorNoReintentable(e),
        });
      }
    } finally {
      if (controladores.get(it.id) === ctrl) controladores.delete(it.id);
    }
  }

  async function bombear(): Promise<void> {
    if (corriendo) return;
    corriendo = true;
    try {
      for (;;) {
        const sig = siguientePendiente(items) as ItemNota | null;
        if (!sig) break;
        await ejecutar(sig);
      }
    } finally {
      corriendo = false;
    }
  }

  return {
    // ── lectura ──
    getItems: (): ItemNota[] => items,
    getAvisoPantalla: (): boolean => avisoPantalla,
    haySubidasActivas: (aseoId?: number): boolean =>
      hayActivos(aseoId == null ? items : items.filter((i) => i.aseoId === aseoId)),
    subscribe: (fn: () => void) => {
      suscriptores.add(fn);
      return () => {
        suscriptores.delete(fn);
      };
    },
    /** Se invoca cada vez que un archivo termina de subir (para refrescar las notas). */
    alTerminar: (fn: (i: ItemNota) => void) => {
      oyentesListo.add(fn);
      return () => {
        oyentesListo.delete(fn);
      };
    },

    // ── acciones ──
    encolar: (p: { aseoId: number; tipo: AseoNoteTipo; file: File }): string => {
      const id = `up-${ahora()}-${++contador}`;
      const item: ItemNota = {
        id,
        aseoId: p.aseoId,
        tipo: p.tipo,
        file: p.file,
        nombre: p.file.name || "archivo",
        mime: mimeDeArchivo(p.file),
        kind: kindDeArchivo(mimeDeArchivo(p.file)),
        total: p.file.size,
        bytes: 0,
        porcentaje: 0,
        velocidadBps: 0,
        etaSeg: null,
        estado: "en_espera",
        mensaje: null,
        intentos: 0,
        previewUrl: crearPreview(p.file),
        creadoEn: ahora(),
      };
      dispatch({ tipo: "encolar", item });
      void bombear();
      return id;
    },
    cancelar: (id: string) => {
      const antes = items.find((i) => i.id === id);
      // Con la subida corriendo, el abort ya borra la sesión remota; sin ella
      // (ítem en error) hay que liberarla aquí.
      controladores.get(id)?.abort();
      dispatch({ tipo: "cancelar", id });
      liberarSesion(antes?.estado === "error" ? antes : undefined);
    },
    reintentar: (id: string) => {
      controladores.delete(id); // invalida cualquier ejecución anterior que siga abortándose
      dispatch({ tipo: "reintentar", id });
      void bombear();
    },
    quitar: (id: string) => {
      liberarSesion(items.find((i) => i.id === id));
      quitarSilencioso(id);
    },
    descartarAvisoPantalla: () => {
      if (avisoPantalla) {
        avisoPantalla = false;
        avisar();
      }
    },
  };
}

export type ColaSubida = ReturnType<typeof crearColaSubida>;

// ─── Procesador real de las notas ────────────────────────────────────────────

/** Comprime las fotos (config admin) y sube por el endpoint de la nota. */
export async function procesarItemNota(item: ItemNota, ctx: ContextoProcesar): Promise<unknown> {
  let file = item.file;
  if (item.kind === "image") {
    ctx.onEstado("comprimiendo");
    try {
      // Sin type (algunos Android) el compresor no reconoce la imagen: se le da el deducido.
      const mime = mimeDeArchivo(item.file);
      const entrada =
        mime && mime !== item.file.type
          ? new File([item.file], item.file.name, { type: mime, lastModified: item.file.lastModified })
          : item.file;
      file = await compressImageFile(entrada);
    } catch (e) {
      // Un HEIC que no se pudo convertir NO se sube tal cual: el servidor no lo
      // acepta y la aseadora vería un error técnico. HeicError ya trae el mensaje.
      if (e instanceof HeicError) throw e;
      // Si no se puede optimizar (formato raro, memoria), se sube el original.
      file = item.file;
    }
    if (ctx.signal.aborted) throw new DOMException("cancelada", "AbortError");
    ctx.onEstado("subiendo");
  }
  return subirArchivoAModulo(rutaArchivosNota(item.aseoId, item.tipo), file, {
    signal: ctx.signal,
    onProgreso: ctx.onProgreso,
  });
}

/** Cola única de la app para las notas de aseo. */
export const colaNotas: ColaSubida = crearColaSubida({
  procesar: procesarItemNota,
  descartar: (item) => descartarSubida(item.file, rutaArchivosNota(item.aseoId, item.tipo)),
});

// ─── Hooks ───────────────────────────────────────────────────────────────────

/** Ítems de la cola del aseo dado (todos los tipos de nota), reactivo. */
export function useColaNotas(aseoId: number | null | undefined) {
  const todos = useSyncExternalStore(colaNotas.subscribe, colaNotas.getItems, colaNotas.getItems);
  const avisoPantalla = useSyncExternalStore(
    colaNotas.subscribe,
    colaNotas.getAvisoPantalla,
    colaNotas.getAvisoPantalla,
  );
  const items = useMemo(
    () => (aseoId == null ? [] : todos.filter((i) => i.aseoId === aseoId)),
    [todos, aseoId],
  );
  const encolar = useCallback(
    (tipo: AseoNoteTipo, file: File) => {
      if (aseoId == null) return null;
      return colaNotas.encolar({ aseoId, tipo, file });
    },
    [aseoId],
  );
  return { items, avisoPantalla, encolar, cola: colaNotas };
}
